/** Korean product copy shared by messenger components (taken from the design). */
import type { MessageView } from '@sd/client-core';
import { LIMITS, type CompanyRole, type ConversationSummary, type EditWindow, type Invitation, type Message, type MessagePreview, type Person, type Reaction, type ReplyRef, type ScanDetail, type ScanStatus, type SystemEvent } from '@sd/contracts';
import type { IconName } from '../icons/Icon';
import { errorMessage } from '../errors';
import { dayKey, formatBytes, formatClock, formatDayDivider, formatListTime } from '../format';
import { plainText } from './markup';

export type PersonLookup = (id: string | null | undefined) => Person | undefined;

const nameOf = (people: PersonLookup, id: string | null | undefined) => people(id)?.displayName ?? '알 수 없는 사용자';

export function systemIcon(event: SystemEvent): IconName {
  switch (event.type) {
    case 'member.added':
      return 'userplus';
    case 'member.left':
      return 'logout';
    case 'member.removed':
      return 'userminus';
    case 'history_mode.changed':
      return 'history';
    case 'title.changed':
      return 'compose';
    case 'owner.changed':
      return 'shield';
    case 'account.deactivated':
      return 'lock';
    case 'direct.started':
      return 'chat';
    case 'group.created':
      return 'users';
    case 'unsupported':
      return 'info';
  }
}

const historyLabel = (mode: 'after_join' | 'include_history') => (mode === 'include_history' ? '기존 내역 포함' : '참여 이후 메시지만');

export function systemText(event: SystemEvent, people: PersonLookup, meId: string, peerId?: string | null): string {
  switch (event.type) {
    case 'member.added': {
      const targets = event.userIds.map((id) => `${nameOf(people, id)}님`).join(', ');
      return event.historyMode === 'include_history'
        ? `${nameOf(people, event.actorId)}님이 ${targets}을 초대했어요. 기존 내역이 함께 공유돼요.`
        : `${nameOf(people, event.actorId)}님이 ${targets}을 초대했어요. ${targets}에게는 참여 이후 메시지만 보여요.`;
    }
    case 'member.left':
      return `${nameOf(people, event.userId)}님이 대화방을 나갔어요. 나간 시점까지의 대화만 읽을 수 있어요.`;
    case 'member.removed':
      return `${nameOf(people, event.actorId)}님이 ${nameOf(people, event.userId)}님을 내보냈어요.`;
    case 'history_mode.changed':
      return `${nameOf(people, event.actorId)}님이 과거 내역 공유를 ‘${historyLabel(event.historyMode)}’으로 바꿨어요.`;
    case 'title.changed':
      return `${nameOf(people, event.actorId)}님이 그룹 이름을 ‘${event.title}’(으)로 바꿨어요.`;
    case 'owner.changed':
      return `${nameOf(people, event.toId)}님이 그룹 소유자가 됐어요.`;
    case 'account.deactivated':
      return `${nameOf(people, event.userId)}님의 계정이 비활성화됐어요. 이 대화는 회사 보존 정책에 따라 읽기 전용으로 남아요.`;
    case 'direct.started':
      if (peerId === meId) return '나만 보는 대화예요. 메모나 파일을 보내 두면 내 다른 기기에서도 볼 수 있어요.';
      return event.actorId === meId ? `${nameOf(people, peerId)}님과의 대화를 시작했어요` : `${nameOf(people, event.actorId)}님이 대화를 시작했어요`;
    case 'group.created':
      return event.fromDirect
        ? '새 그룹 대화를 만들었어요. 기존 1:1 내역은 이 그룹에 공유되지 않아요.'
        : `그룹 대화를 만들었어요. 그룹 소유자는 ${nameOf(people, event.actorId)}님이에요.`;
    case 'unsupported':
      return UNSUPPORTED_TEXT;
  }
}

/** Messages and system lines added by a newer server than this app knows. */
export const UNSUPPORTED_TEXT = '앱을 업데이트하면 볼 수 있는 내용이에요.';

/** List preview text (without the draft / failed / read-only prefixes). */
export function previewText(conversation: ConversationSummary, preview: MessagePreview | null, people: PersonLookup, meId: string): string {
  if (!preview) return '';
  let body: string;
  switch (preview.kind) {
    case 'deleted':
      body = '삭제된 메시지예요';
      break;
    case 'system':
      return preview.system ? systemText(preview.system, people, meId, conversation.peer?.id) : '';
    case 'attachment':
      body = `[파일] ${preview.attachmentName ?? ''}${preview.attachmentCount > 1 ? ` 외 ${preview.attachmentCount - 1}개` : ''}`;
      break;
    case 'unsupported':
      body = UNSUPPORTED_TEXT;
      break;
    default:
      body = plainText(preview.text ?? '').replace(/\s+/g, ' ');
  }
  if (conversation.kind === 'group' && preview.senderId && preview.senderId !== meId) return `${nameOf(people, preview.senderId)}: ${body}`;
  return body;
}

/** What a screen reader says when a message from someone else arrives in the open conversation: "강태오: 점심 같이 드실래요?" */
export function arrivalText(message: Message, people: PersonLookup): string {
  const files = message.attachments;
  const body = message.text?.trim()
    ? message.text.replace(/\s+/g, ' ').slice(0, 200)
    : files[0]
      ? `[파일] ${files[0].name}${files.length > 1 ? ` 외 ${files.length - 1}개` : ''}`
      : '';
  return `${nameOf(people, message.senderId)}: ${body}`;
}

/** An own message's send state as shown next to it and said after it ("전송됨", "읽음 3명" …). */
export function sendStatusText(view: MessageView, conversation: ConversationSummary): string | null {
  switch (view.state) {
    case 'queued':
      return view.lastError === 'MAINTENANCE' ? '대기 · 점검이 끝나면 전송' : '대기 · 연결되면 전송';
    case 'sending':
      return view.upload ? '파일 올리는 중' : '전송 중';
    case 'sent':
      return '전송됨';
    case 'read': {
      if (conversation.kind === 'direct') return '읽음';
      const receipt = view.receipt;
      if (receipt && receipt.eligibleCount > 0 && receipt.readCount >= receipt.eligibleCount) return '모두 읽음';
      return `읽음 ${receipt?.readCount ?? 0}명`;
    }
    case 'failed':
      return '전송 실패';
    default:
      return null;
  }
}

function replyLabel(reply: ReplyRef, people: PersonLookup, meId: string): string {
  if (reply.state === 'deleted') return '삭제된 메시지에 대한 답장';
  if (reply.state === 'unavailable') return '볼 수 없는 메시지에 대한 답장';
  return `${reply.senderId === meId ? '나' : nameOf(people, reply.senderId)}에게 답장, ${reply.excerpt ?? reply.attachmentName ?? ''}`;
}

/**
 * A message as a screen reader says it when it gets focus: sender, time, text, files and the message it
 * answers, then an own message's state ("이도윤, 오전 10:21, 그 부분은 …, 정민재에게 답장, 다음 주 …").
 * The time has its day unless the message is from today.
 */
export function messageLabel(view: MessageView, context: { sender: string; status: string | null; people: PersonLookup; meId: string; now: Date; saved?: boolean }): string {
  const clock = formatClock(view.createdAt);
  const parts = [context.sender, dayKey(view.createdAt) === dayKey(context.now) ? clock : `${formatDayDivider(view.createdAt, context.now)} ${clock}`];
  if (view.deleted) {
    parts.push('삭제된 메시지예요');
  } else {
    if (view.forwarded) parts.push('전달된 메시지');
    if (view.text) parts.push(plainText(view.text).replace(/\s+/g, ' ').trim());
    if (view.edited) parts.push('수정됨');
    for (const file of view.attachments) {
      parts.push(`${file.kind === 'image' ? '사진' : '파일'} ${file.name} ${formatBytes(file.size)}${file.scan === 'clean' ? '' : ` ${scanLabel(file.scan).text}`}${file.executable ? ' 실행 파일' : ''}`);
    }
    if (view.upload) parts.push(`파일 ${view.upload.name} ${formatBytes(view.upload.size)}`);
    for (const preview of view.linkPreviews) parts.push(`링크 미리보기 ${preview.title}`);
    if (view.reply) parts.push(replyLabel(view.reply, context.people, context.meId));
  }
  if (context.status) parts.push(context.status);
  if (context.saved) parts.push('저장됨');
  if (view.reactions.length) parts.push(`반응 ${view.reactions.map((r) => `${r.emoji} ${r.count}명`).join(', ')}`);
  return parts.join(', ');
}

/** A reaction chip as a screen reader says it: "👍 3명, 나 포함". */
export function reactionLabel(reaction: Reaction): string {
  return `${reaction.emoji} ${reaction.count}명${reaction.me ? ', 나 포함' : ''}`;
}

/** Who is behind a chip, the viewer as 나: "나, 김서연 외 3명". */
export function reactorNames(reaction: Reaction, people: PersonLookup, meId: string, max: number = LIMITS.reactionPreviewUsers): string {
  const names = reaction.userIds.slice(0, max).map((id) => (id === meId ? '나' : nameOf(people, id)));
  const more = reaction.count - names.length;
  return more > 0 ? `${names.join(', ')} 외 ${more}명` : names.join(', ');
}

/** Toast after a reaction the server did not take; the chip is back as it was. */
export function reactionFailedMessage(code: string, adding: boolean): string {
  if (code === 'NETWORK') return adding ? '연결되지 않아 반응을 남기지 못했어요' : '연결되지 않아 반응을 취소하지 못했어요';
  if (code === 'READ_ONLY_CONVERSATION') return '읽기 전용 대화라 반응을 남길 수 없어요';
  return errorMessage(code);
}

/** A direct conversation with oneself ("나와의 채팅"): the server gives the viewer as its only member and its peer. */
export function isSelfChat(conversation: ConversationSummary): boolean {
  return conversation.kind === 'direct' && conversation.memberCount === 1 && conversation.peer !== null;
}

export function conversationName(conversation: ConversationSummary): string {
  if (isSelfChat(conversation)) return '나와의 채팅';
  return conversation.title ?? conversation.peer?.displayName ?? '';
}

/**
 * A conversation in the list as a screen reader says it: what it is and whether it needs attention first,
 * then the preview and the time ("디자인시스템 TF, 멤버 6명, 안 읽은 메시지 2개, 나를 멘션함, 정민재: …, 오전 10:31").
 */
export function conversationLabel(conversation: ConversationSummary, preview: string, time: string): string {
  const parts = [conversationName(conversation)];
  if (conversation.kind === 'group') parts.push(`멤버 ${conversation.memberCount}명`);
  if (conversation.unreadCount) parts.push(`안 읽은 메시지 ${conversation.unreadCount}개`);
  if (conversation.mentioned) parts.push('나를 멘션함');
  if (conversation.my.muted) parts.push('음소거됨');
  if (preview) parts.push(preview);
  parts.push(time);
  return parts.join(', ');
}

export const ROLE_LABEL: Record<CompanyRole, string> = { owner: '회사 소유자', admin: '회사 관리자', member: '구성원' };

/** Why the scanner could not inspect a file: an encrypted archive, or one too large or too deep for it (archive_limits). */
export function unscannableReason(detail: ScanDetail | null | undefined): string {
  return detail === 'encrypted_archive' ? '암호화된 압축 파일이라' : '크기나 구조 때문에';
}

export function scanLabel(scan: ScanStatus): { cls: 'ok' | 'wait' | 'warn' | 'bad'; icon: IconName; text: string; spin?: boolean } {
  switch (scan) {
    case 'clean':
      return { cls: 'ok', icon: 'checkc', text: '검사 완료' };
    case 'pending':
    case 'scanning':
      return { cls: 'wait', icon: 'sync', text: '검사 중', spin: true };
    case 'unscannable':
      return { cls: 'warn', icon: 'alert', text: '검사 불가' };
    case 'infected':
      return { cls: 'bad', icon: 'alertc', text: '악성코드 발견' };
    case 'error':
      return { cls: 'bad', icon: 'alertc', text: '검사 오류' };
  }
}

/** Invitation status chip (design INV_ST) from invitation + mail state. */
export function invitationChip(invitation: Invitation): { tone: '' | 'accent' | 'blue' | 'ok' | 'warn' | 'danger'; label: string; tip: string } {
  switch (invitation.status) {
    case 'REQUESTED':
      return { tone: 'warn', label: '승인 대기', tip: '관리자가 승인하면 발송돼요' };
    case 'ACCEPTED':
      return { tone: 'accent', label: '수락됨', tip: '' };
    case 'EXPIRED':
      return { tone: '', label: '만료됨', tip: '유효기간 7일이 지났어요' };
    case 'REVOKED':
      return { tone: '', label: '취소됨', tip: '' };
    case 'PENDING':
      switch (invitation.mailStatus) {
        case null:
          // A server without mail: the link went to whoever issued it, to hand over.
          return { tone: 'ok', label: '링크 발급', tip: '메일 없이 만든 1회용 링크예요. 받는 사람이 가입하면 수락됨으로 바뀌어요' };
        case 'ACCEPTED_BY_PROVIDER':
          return { tone: 'ok', label: '메일 서비스 접수', tip: '메일 서비스가 발송을 접수했어요. 수신함 도착 여부는 알 수 없어요' };
        case 'FAILED':
          return { tone: 'danger', label: '발송 실패', tip: '' };
        case 'BOUNCED':
          return { tone: 'danger', label: '반송됨', tip: '메일 서비스가 반송을 알려왔어요' };
        default:
          return { tone: 'blue', label: '발송 대기', tip: '메일 서비스로 보내기 전이에요' };
      }
  }
}

const monthDay = (iso: string) => {
  const d = new Date(iso);
  return `${d.getMonth() + 1}월 ${d.getDate()}일`;
};

/** "오늘 오전 9:12", "10월 8일 · 10월 15일 만료", "10월 9일 수락" … */
export function invitationWhen(invitation: Invitation, now: Date): string {
  const sameDay = (iso: string) => new Date(iso).toDateString() === now.toDateString();
  const stamp = (iso: string) => (sameDay(iso) ? `오늘 ${formatClock(iso)}` : formatListTime(iso, now));
  switch (invitation.status) {
    case 'REQUESTED':
      return stamp(invitation.createdAt);
    case 'ACCEPTED':
      return `${monthDay(invitation.acceptedAt ?? invitation.createdAt)} 수락`;
    case 'EXPIRED':
      return `${monthDay(invitation.expiresAt ?? invitation.createdAt)} 만료`;
    case 'REVOKED':
      return `${monthDay(invitation.revokedAt ?? invitation.createdAt)} 취소`;
    case 'PENDING':
      if (invitation.mailStatus === 'BOUNCED') return `${monthDay(invitation.sentAt ?? invitation.createdAt)} · 주소를 확인해 주세요`;
      if ((invitation.mailStatus === 'ACCEPTED_BY_PROVIDER' || invitation.mailStatus === null) && invitation.expiresAt) return `${monthDay(invitation.sentAt ?? invitation.createdAt)} · ${monthDay(invitation.expiresAt)} 만료`;
      return stamp(invitation.createdAt);
  }
}

/** Upload limits are round numbers: "100MB", "1.5GB". */
export function sizeLimitText(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  return gb >= 1 ? `${Number(gb.toFixed(1))}GB` : `${Number((bytes / 1024 ** 2).toFixed(1))}MB`;
}

/** "파일당 최대 100MB · 한 번에 10개", from the limits the server announced. */
export function uploadLimitsText(limits: { maxFileBytes: number; maxFilesPerMessage: number }): string {
  return `파일당 최대 ${sizeLimitText(limits.maxFileBytes)} · 한 번에 ${limits.maxFilesPerMessage}개`;
}

const EDIT_WINDOW_SPAN: Record<EditWindow, string | null> = { '1h': '1시간', '24h': '24시간', '7d': '7일', none: null };

/** "24시간 이내" for the company's edit and delete window, or null when it is unlimited. */
export function editWindowText(window: EditWindow): string | null {
  const span = EDIT_WINDOW_SPAN[window];
  return span ? `${span} 이내` : null;
}

/** Toast after a failed edit; the edit stays open with what was typed. */
export function editFailedMessage(code: string): string {
  if (code === 'NETWORK') return '연결되지 않아 수정하지 못했어요. 입력한 내용은 그대로 있어요';
  if (code === 'REVISION_CONFLICT') return '다른 곳에서 먼저 수정됐어요. 최신 내용을 확인한 뒤 다시 저장해 주세요';
  return errorMessage(code);
}
